// Pure helpers for the quotation editor's pop-out window (#1430). Kept separate from the hook so
// the URL contract and the window-placement maths are unit-testable without a DOM.
//
// Naming: the design and the menu row say "new tab", the code says `popOut`, and what actually
// opens is a WINDOW. All three are deliberate — see the issue's requirement 1.

import { POP_OUT_OWNER_PARAM } from "helpers/popOutWindowChannel.helper"

// The route the pop-up boots. It is a REAL route in this app, not a blank window we inject into:
// that gives the child its own document and JS realm, which is what keeps its Apryse instance from
// colliding with the opener's (#1119 is a per-realm invariant).
export const POP_OUT_WINDOW_PATH = "/quotation-window"

// Named target, so re-opening focuses the existing window instead of stacking a second one.
export const POP_OUT_WINDOW_NAME = "asq-quotation-window"

// Are WE the pop-out? Constant for the session — this window is booted on the route and never
// navigates away from it, and the query-string strip (see `resolveViewerSearch` below) deliberately
// leaves the PATH alone so this stays true for the window's whole life.
//
// It has to be session-constant for two separate reasons:
//   1. the header captures its props once, so only a constant can decide what a row does at all;
//   2. `useSplitScreen.canSplit` reads it, and the in-window dual view must be off in the child
//      from the first render rather than becoming unavailable later (#1430, AC4).
//
// The child must also never offer to pop out again: `window.open` with our named target, called
// from the window that already HOLDS that name, navigates this window onto itself rather than
// opening a third.
export const isPopOutWindowRoute = () =>
	typeof window !== "undefined" && window.location.pathname === POP_OUT_WINDOW_PATH

export type TPopOutWindowParams = {
	drawingId: number
	processingId: number | null
	quotationId: number | null
	drawingStatus: string
	// Present only for the CHILD — see popOutWindowChannel.helper's header.
	//
	// This has to travel through the SAME `readPopOutWindowParams` / `resolveViewerSearch` round trip
	// as the other ids, not a plain `new URLSearchParams(window.location.search)` read at the point
	// `usePopOutWindow` mounts: that mount is gated behind two async fetches (`useDocuments` →
	// `usePresignedDownloadUrl`) inside `PageQuotationWindow`, which run well after that page's OWN
	// effect has already stripped the query string. A raw read at mount time therefore always misses
	// the real opener id and mints a random one instead (#1430 review) — silently breaking the WHOLE
	// control channel for the child, not just a feature that reads it: `return-view-back` posted with
	// the wrong id is filtered out by the opener as "not mine", so clicking it in the pop-out just
	// closes the window without ever handing the in-window split back. `resolveViewerSearch()` already
	// solves exactly this race for `drawingID`/`quotationID`/etc. by falling back to sessionStorage
	// once the live URL comes back stripped; `usePopOutWindow` reads the opener id the same way.
	openerId?: string
}

// Everything the child needs to fetch for itself. Deliberately IDs only — never the presigned PDF
// URL. Those expire, and a stale one baked into a window the user leaves open for an hour would
// fail to load with no way to recover short of reopening. The child resolves its own download URL
// through the same hooks the opener uses.
export const buildPopOutWindowUrl = (origin: string, params: TPopOutWindowParams, openerId?: string): string => {
	const url = new URL(POP_OUT_WINDOW_PATH, origin)
	url.searchParams.set("drawingID", String(params.drawingId))
	if (params.processingId != null) url.searchParams.set("processingID", String(params.processingId))
	if (params.quotationId != null) url.searchParams.set("quotationID", String(params.quotationId))
	url.searchParams.set("drawingStatus", params.drawingStatus)
	// Which editor session owns this window, so its control-channel traffic can be addressed back to
	// exactly that opener and ignored by any other editor tab. Optional because the id is not part of
	// the DATA contract — `parsePopOutWindowParams` neither reads nor requires it, and rebuilding a
	// URL for display (the feedback link) has no opener to name.
	if (openerId) url.searchParams.set(POP_OUT_OWNER_PARAM, openerId)
	return url.toString()
}

// The child's side of that contract: read the ids back out of a query string.
//
// `null` is a REACHABLE state, not a defensive branch: the child strips its own query string the
// moment it has read it, so a user who copies the URL out of the pop-up — the thing requirement 2
// asks us to make useless — and pastes it into a normal tab arrives with nothing.
export const parsePopOutWindowParams = (search: string): TPopOutWindowParams | null => {
	const params = new URLSearchParams(search)
	const drawingId = Number(params.get("drawingID"))
	if (!Number.isFinite(drawingId) || drawingId <= 0) return null
	return {
		drawingId,
		processingId: Number(params.get("processingID")) || null,
		quotationId: Number(params.get("quotationID")) || null,
		drawingStatus: params.get("drawingStatus") ?? "",
		openerId: params.get(POP_OUT_OWNER_PARAM) ?? undefined,
	}
}

// Where the child keeps its ids once they are out of the URL.
//
// `sessionStorage` is the right store for exactly one reason: it is scoped to the BROWSING CONTEXT.
// The ids stay readable across a reload of this pop-up (Cmd+R still works even with no toolbar,
// and without this the reload would land on a bare URL and bounce the user to /all-files
// mid-edit), while a copied link pasted into any other tab or window gets its own empty store and
// is still useless — which is the whole point of stripping the URL. `localStorage` would be shared
// origin-wide and would defeat that; the URL itself is what requirement 2 asks us to empty.
export const POP_OUT_PARAMS_STORAGE_KEY = "asq-pop-out-params"

// Read the ids from the URL, falling back to what a previous boot of THIS window stored, and keep
// the store in step. Storage is passed in rather than reached for so this stays a pure-ish function
// the unit tests can drive with a fake — and so a browser that refuses storage (private mode
// quotas, blocked site data) degrades to URL-only instead of throwing on boot.
export const readPopOutWindowParams = (
	search: string,
	storage: Pick<Storage, "getItem" | "setItem"> | null,
): TPopOutWindowParams | null => {
	const fromUrl = parsePopOutWindowParams(search)
	if (fromUrl) {
		try {
			storage?.setItem(POP_OUT_PARAMS_STORAGE_KEY, JSON.stringify(fromUrl))
		} catch {
			// Storage unavailable or full — the URL already gave us what we need for this boot; only a
			// later reload loses out, which is strictly better than failing to open at all.
		}
		return fromUrl
	}

	try {
		const stored = storage?.getItem(POP_OUT_PARAMS_STORAGE_KEY)
		if (!stored) return null
		const parsed = JSON.parse(stored) as TPopOutWindowParams
		// Validate rather than trust: this string is user-writable through DevTools, and a bad
		// drawingId would otherwise reach the API as `NaN`.
		if (typeof parsed?.drawingId !== "number" || !Number.isFinite(parsed.drawingId) || parsed.drawingId <= 0) {
			return null
		}
		return {
			drawingId: parsed.drawingId,
			processingId: typeof parsed.processingId === "number" ? parsed.processingId : null,
			quotationId: typeof parsed.quotationId === "number" ? parsed.quotationId : null,
			drawingStatus: typeof parsed.drawingStatus === "string" ? parsed.drawingStatus : "",
			// Carried through the reload path too: a refreshed pop-out must keep talking to the SAME
			// opener, not mint a fresh id and silently stop being addressable on the control channel.
			openerId: typeof parsed.openerId === "string" ? parsed.openerId : undefined,
		}
	} catch {
		// Unreadable or malformed store — treat it as absent, which lands on the /all-files redirect.
		return null
	}
}

// The inverse of `buildPopOutWindowUrl`'s query half — used to REBUILD a search string from stored
// params, see `resolveViewerSearch` below.
export const popOutParamsToSearch = (params: TPopOutWindowParams): string => {
	const search = new URLSearchParams()
	search.set("drawingID", String(params.drawingId))
	if (params.processingId != null) search.set("processingID", String(params.processingId))
	if (params.quotationId != null) search.set("quotationID", String(params.quotationId))
	search.set("drawingStatus", params.drawingStatus)
	// Without this, `resolveViewerSearch()` rebuilds a search string on every reload that has the
	// drawing/quotation ids but not the opener id — `usePopOutWindow`'s own lazy initializer reads
	// this exact string, so omitting it here would mint the child a brand-new random id on every
	// reload and silently break the control channel for the rest of that boot.
	if (params.openerId) search.set(POP_OUT_OWNER_PARAM, params.openerId)
	return `?${search.toString()}`
}

// **The search string the editor should behave as if it were on.**
//
// The editor resolves the drawing / processing / quotation ids from the URL in a dozen places — the
// orchestrator, and every right-sidebar component through the `MemoryRouter` that
// `ShadowDomProviders` seeds. In the pop-out that URL is deliberately stripped after boot
// (requirement 2), and the shadow-DOM sidebars are `createRoot`ed LATER, on each panel open — so
// they would seed from an empty query and resolve `Number(null) === 0`, addressing every write to
// `/quotations/0/…`. Measured: reads worked (the orchestrator had the id from before the strip)
// while every save 500'd, silently.
//
// So the ids live in this window's `sessionStorage` and this is the one accessor that knows to look
// there. Two properties make it safe to call from anywhere:
//   - it is a NO-OP off the pop-out route, returning the live search untouched, so the main app's
//     behaviour is byte-for-byte unchanged;
//   - it prefers the live URL whenever that still carries ids, so the fresh-boot path does not
//     depend on the order the strip and the sidebar mounts happen to run in.
// It also removes a subtler dependency: the fresh-boot path used to work only because a raw
// `history.replaceState` is invisible to react-router (its history listens for `popstate` and wraps
// its own push/replace), so `useSearchParams` kept the pre-strip ids. That is true today and is not
// a thing to rely on — with this fallback in place, the ids resolve either way.
export const resolveViewerSearch = (): string => {
	if (typeof window === "undefined") return ""
	const live = window.location.search
	// Ids still in the URL (the main app always, the pop-out until it strips) — nothing to resolve.
	if (parsePopOutWindowParams(live)) return live
	if (!isPopOutWindowRoute()) return live
	const stored = readPopOutWindowParams("", typeof sessionStorage === "undefined" ? null : sessionStorage)
	return stored ? popOutParamsToSearch(stored) : live
}

export type TWindowPlacement = { width: number; height: number; left: number; top: number }

// Chrome's Window Management API. Not in the TS DOM lib yet, so declared narrowly — only the
// fields we read.
type TScreenDetail = {
	availWidth: number
	availHeight: number
	availLeft: number
	availTop: number
	isPrimary: boolean
}
type TScreenDetails = { screens: TScreenDetail[]; currentScreen: TScreenDetail }
type TWindowWithScreenDetails = Window & { getScreenDetails?: () => Promise<TScreenDetails> }

// Leave a margin so the window reads as a window rather than a second fullscreen app.
const SCREEN_INSET = 40

export const placementForScreen = (screen: {
	availWidth: number
	availHeight: number
	availLeft: number
	availTop: number
}): TWindowPlacement => ({
	width: Math.max(screen.availWidth - SCREEN_INSET * 2, 480),
	height: Math.max(screen.availHeight - SCREEN_INSET * 2, 360),
	left: screen.availLeft + SCREEN_INSET,
	top: screen.availTop + SCREEN_INSET,
})

// Fallback when we cannot see the monitor layout: half the current screen, parked on its right.
// The user can still drag it across — this just avoids opening exactly on top of the editor.
export const fallbackPlacement = (currentScreen: {
	availWidth: number
	availHeight: number
	availLeft?: number
	availTop?: number
}): TWindowPlacement => {
	const left = currentScreen.availLeft ?? 0
	const top = currentScreen.availTop ?? 0
	const width = Math.max(Math.round(currentScreen.availWidth / 2), 480)
	return {
		width,
		height: Math.max(currentScreen.availHeight - SCREEN_INSET, 360),
		left: left + currentScreen.availWidth - width,
		top,
	}
}

// Pick the monitor to open on: the first one that is NOT the monitor the editor is currently on.
// That is the whole point of a real window — an in-app pane can never leave the browser window, so
// if there is a second monitor, this is what puts the view on it.
export const pickTargetScreen = (details: TScreenDetails): TScreenDetail =>
	details.screens.find((screen) => screen !== details.currentScreen) ?? details.currentScreen

// Resolve where to open. Uses the Window Management API when the browser exposes it AND the user
// has granted it; otherwise falls back silently. Never throws — a placement is a nicety, and
// failing to compute one must not stop the window opening.
//
// It is `async`, which is exactly why it must NOT be awaited before `window.open`: `getScreenDetails`
// can block on a permission prompt, and any await spends the click's user-activation token, after
// which the browser blocks the pop-up. `usePopOutWindow` opens first and moves the window after.
export const resolveWindowPlacement = async (win: Window): Promise<TWindowPlacement> => {
	const getScreenDetails = (win as TWindowWithScreenDetails).getScreenDetails
	if (typeof getScreenDetails === "function") {
		try {
			const details = await getScreenDetails.call(win)
			if (details?.screens?.length > 1) return placementForScreen(pickTargetScreen(details))
		} catch {
			// Permission denied or unsupported in this context — fall through to the single-screen path.
		}
	}
	// `win.screen` is always present in a real browser, but this function's contract is that it
	// NEVER throws — a placement is a nicety and must not be able to stop the window opening — so
	// the last resort is a fixed size rather than a dereference.
	return win.screen ? fallbackPlacement(win.screen) : { width: 1200, height: 800, left: 0, top: 0 }
}

export const toWindowFeatures = (placement: TWindowPlacement): string =>
	[
		`width=${Math.round(placement.width)}`,
		`height=${Math.round(placement.height)}`,
		`left=${Math.round(placement.left)}`,
		`top=${Math.round(placement.top)}`,
		// `popup=yes` is what makes Chrome give a real, separate window rather than a tab — and it is
		// also the whole of the answer to requirement 2: a pop-up has no tab strip, no toolbar and no
		// EDITABLE address bar, only a non-editable origin strip that no API can remove. The ids are
		// kept out of the copyable URL separately, by stripping them after boot.
		"popup=yes",
		// The opener reference is what lets it close the child on teardown and place it after opening.
		"noopener=no",
		"resizable=yes",
		"scrollbars=yes",
	].join(",")
